import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  CircularProgress,
  Chip,
  Stack,
  InputAdornment,
} from "@mui/material";
import PaymentsIcon from "@mui/icons-material/Payments";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { expensePaymentAPI, PaymentSuggestion } from "../../services/api/api";
import { getTodayLocal } from "../../lib/dateUtils";

interface RecordPaymentDialogProps {
  open: boolean;
  expenseId: string;
  remainingBalance: number;
  onCancel: () => void;
  onSaved: () => void;
}

interface LinkedTransaction {
  id: string;
  label: string;
}

const toDateInputValue = (date: Date): string =>
  date.toISOString().slice(0, 10);

const RecordPaymentDialog: React.FC<RecordPaymentDialogProps> = ({
  open,
  expenseId,
  remainingBalance,
  onCancel,
  onSaved,
}) => {
  const [amount, setAmount] = useState<string>("");
  const [paymentDate, setPaymentDate] = useState<string>(
    toDateInputValue(getTodayLocal())
  );
  const [paymentMethod, setPaymentMethod] = useState("");
  const [notes, setNotes] = useState("");
  const [linkedTransaction, setLinkedTransaction] =
    useState<LinkedTransaction | null>(null);
  const [suggestions, setSuggestions] = useState<PaymentSuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setAmount(remainingBalance > 0 ? remainingBalance.toFixed(2) : "");
      setPaymentDate(toDateInputValue(getTodayLocal()));
      setPaymentMethod("");
      setNotes("");
      setLinkedTransaction(null);
      setSearchQuery("");
      setError(null);
    }
  }, [open, remainingBalance]);

  useEffect(() => {
    if (!open || !expenseId) return;
    let cancelled = false;
    setLoadingSuggestions(true);
    console.log("[RecordPaymentDialog] Fetching suggestions", {
      expenseId,
      paymentDate,
      q: searchQuery,
    });
    expensePaymentAPI
      .suggestions(expenseId, {
        paymentDate,
        q: searchQuery || undefined,
        windowDays: 21,
        limit: 15,
      })
      .then((res) => {
        if (cancelled) return;
        setSuggestions(res.suggestions || []);
      })
      .catch((err) => {
        console.error("[RecordPaymentDialog] suggestions error", err);
      })
      .finally(() => {
        if (!cancelled) setLoadingSuggestions(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, expenseId, paymentDate, searchQuery]);

  const parsedAmount = Number(amount);
  const amountValid = Number.isFinite(parsedAmount) && parsedAmount > 0;

  const handleSelectTransaction = (s: PaymentSuggestion) => {
    const t = s.transaction;
    setLinkedTransaction({
      id: t.transaction_id,
      label: t.merchant_name || t.name || t.transaction_id,
    });
    if (!amount || Number(amount) === 0) {
      setAmount(Math.abs(Number(t.amount || 0)).toFixed(2));
    }
  };

  const handleSave = async () => {
    if (!amountValid) {
      setError("Enter a valid payment amount");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      console.log("[RecordPaymentDialog] Saving payment", {
        expenseId,
        amount: parsedAmount,
        paymentDate,
        linkedTransactionId: linkedTransaction?.id ?? null,
      });
      await expensePaymentAPI.create(expenseId, {
        amount: parsedAmount,
        paymentDate,
        paymentMethod: paymentMethod || null,
        notes: notes || null,
        linkedTransactionId: linkedTransaction?.id ?? null,
      });
      onSaved();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save payment";
      console.error("[RecordPaymentDialog] save error", err);
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <PaymentsIcon fontSize="small" />
          <span>Record Payment</span>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {remainingBalance > 0 && (
            <Typography variant="body2" color="text.secondary">
              Remaining balance: ${remainingBalance.toFixed(2)}
            </Typography>
          )}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
              }}
            />
            <TextField
              label="Date"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
          <TextField
            label="Method (optional)"
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            placeholder="e.g. Visa ••1234, ACH, Cash"
            fullWidth
          />
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Link to a transaction (optional)
            </Typography>
            {linkedTransaction ? (
              <Chip
                icon={<LinkIcon />}
                label={linkedTransaction.label}
                onDelete={() => setLinkedTransaction(null)}
                deleteIcon={<LinkOffIcon />}
                color="primary"
                sx={{ mb: 1 }}
              />
            ) : (
              <TextField
                size="small"
                fullWidth
                placeholder="Search transactions by name or merchant"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ mb: 1 }}
              />
            )}

            {!linkedTransaction && (
              <Paper
                variant="outlined"
                sx={{ maxHeight: 240, overflow: "auto" }}
              >
                {loadingSuggestions ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      p: 2,
                    }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                ) : suggestions.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ p: 2 }}
                  >
                    No matching transactions found nearby.
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {suggestions.map((s) => {
                      const t = s.transaction;
                      const amt = Math.abs(Number(t.amount || 0));
                      const label = t.merchant_name || t.name || "Unnamed";
                      const dateStr = t.date
                        ? new Date(t.date).toLocaleDateString()
                        : "";
                      return (
                        <ListItemButton
                          key={t.transaction_id}
                          onClick={() => handleSelectTransaction(s)}
                        >
                          <ListItemText
                            primary={`${label} — $${amt.toFixed(2)}`}
                            secondary={`${dateStr} • ${s.daysOff.toFixed(0)}d off, $${s.amountDiff.toFixed(2)} diff`}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                )}
              </Paper>
            )}
          </Box>

          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !amountValid}
        >
          {saving ? "Saving…" : "Save Payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordPaymentDialog;
